import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';
import type { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import type { HelpVideoItem } from '@ardoq/page-layout';
import type { TourId } from '@ardoq/product-tours';
import { startTour } from '@ardoq/product-tours-host';
import { dispatchAction } from '@ardoq/rxbeach';
import type {
  TrackedHelpActionableItem,
  TrackedHelpItem,
} from '../trackedHelpItems';

type WithoutSection<T> = T extends unknown ? Omit<T, 'section'> : never;

export type PageHelpItem = WithoutSection<TrackedHelpActionableItem>;

export type PageHelpPanelHeader = {
  label: string;
  description?: string;
};

export type PageHelpArticle = {
  label: string;
  description?: string;
  href: KnowledgeBaseLink;
};

export type PageHelp = {
  panelHeader: PageHelpPanelHeader | null;
  whatsNew: PageHelpItem[];
  articles: PageHelpArticle[];
  tours: PageHelpItem[];
  videos: HelpVideoItem[];
};

type ProductTourItemArgs = {
  tourId: TourId;
  label: string;
  description?: string;
};

export const toProductTourItem = ({
  tourId,
  label,
  description,
}: ProductTourItemArgs): PageHelpItem => ({
  type: 'action',
  label,
  description,
  trackingId: tourId,
  onClick: () => dispatchAction(startTour(tourId)),
});

/** The last path segment, e.g. `44160-what-is-a-workspace`. */
const toKnowledgeBaseArticleId = (href: KnowledgeBaseLink): string =>
  new URL(href).pathname.split('/').filter(isTruthy).at(-1) ?? href;

const toPanelHeaderItems = ({
  label,
  description,
}: PageHelpPanelHeader): TrackedHelpItem[] => [
  { type: 'panel-header', label, description },
];

const toArticleItem = ({
  label,
  description,
  href,
}: PageHelpArticle): TrackedHelpItem => ({
  type: 'link',
  label,
  trackingId: toKnowledgeBaseArticleId(href),
  description,
  href,
  target: '_blank',
  section: 'article',
});

const toWhatsNewItem = (item: PageHelpItem): TrackedHelpItem => ({
  ...item,
  section: 'whats-new',
});

const toTourItem = (item: PageHelpItem): TrackedHelpItem => ({
  ...item,
  section: 'tour',
});

const toTourItems = (
  i18nLocale: I18nLocale,
  tours: PageHelpItem[]
): TrackedHelpItem[] => {
  if (tours.length === 0) {
    return [];
  }
  const headerLabel =
    tours.length === 1
      ? t(
          i18nLocale,
          'views:navbar.helpItem.productTourHeaderSingular',
          'Product tour'
        )
      : t(
          i18nLocale,
          'views:navbar.helpItem.productTourHeaderPlural',
          'Product tours'
        );
  return [{ type: 'header', label: headerLabel }, ...tours.map(toTourItem)];
};

export const toHelpItems = (
  i18nLocale: I18nLocale,
  { panelHeader, whatsNew, articles, tours, videos }: PageHelp
): TrackedHelpItem[] => [
  ...(panelHeader ? toPanelHeaderItems(panelHeader) : []),
  ...articles.map(toArticleItem),
  ...whatsNew.map(toWhatsNewItem),
  ...toTourItems(i18nLocale, tours),
  ...videos,
];
